import { Link } from '@chakra-ui/next-js';
import { Box, Flex, Grid, GridItem, Text } from '@chakra-ui/react';

import { AndroidMock } from '$components/ui/device-mock/android';
import { SafariMock } from '$components/ui/device-mock/safari';

export type Category = 'web' | 'mobile' | 'api' | 'other';

export interface Project {
  slug: string;
  name: string;
  description: string;
  category: Category;
  url?: string | null;
  repository?: string | null;
  techs: string[];
  thumbnail?: string | null;
}

const categoryLabel: Record<Category, string> = {
  web: 'Web',
  mobile: 'Mobile',
  api: 'API',
  other: 'Software',
};

interface ProjectCardProps {
  project: Project;
  index?: number;
}

export function ProjectCard({ project, index }: ProjectCardProps) {
  const href = `/projects/${project.slug}`;
  const indexLabel =
    typeof index === 'number' ? String(index + 1).padStart(2, '0') : null;

  const isMobileCategory = project.category === 'mobile';
  const thumbWidthMd = isMobileCategory ? '120px' : '200px';
  const thumbHeightMd = isMobileCategory ? '210px' : '120px';

  return (
    <Link
      href={href}
      role="group"
      display="block"
      py={{ base: '20px', md: '24px' }}
      borderTopWidth="1px"
      borderColor="line.subtle"
      transition="background 0.25s ease, border-color 0.25s ease"
      borderRadius="10px"
      px="14px"
      mx="-14px"
      _hover={{
        textDecoration: 'none',
        bg: 'bg.surface',
        borderColor: 'transparent',
      }}
      _last={{ borderBottomWidth: '1px' }}
    >
      <Grid
        templateColumns={{
          base: '1fr',
          md: `80px 1fr ${thumbWidthMd}`,
        }}
        templateAreas={{
          base: `"thumb" "content"`,
          md: `"index content thumb"`,
        }}
        gap={{ base: '14px', md: '24px' }}
        alignItems="center"
      >
        <GridItem gridArea="index" display={{ base: 'none', md: 'block' }}>
          <Flex
            fontFamily="mono"
            fontSize="11px"
            letterSpacing="0.12em"
            textTransform="uppercase"
            color="text.faint"
            gap="10px"
            align="center"
            _groupHover={{ color: 'accent.500' }}
            transition="color 0.25s ease"
          >
            {indexLabel && <Text as="span">{indexLabel}</Text>}
            <Text as="span">{categoryLabel[project.category]}</Text>
          </Flex>
        </GridItem>

        <GridItem gridArea="content">
          {/* Mobile-only meta line (index + category) above the title */}
          <Flex
            display={{ base: 'flex', md: 'none' }}
            fontFamily="mono"
            fontSize="11px"
            letterSpacing="0.12em"
            textTransform="uppercase"
            color="text.faint"
            gap="10px"
            mb="6px"
          >
            {indexLabel && <Text as="span">{indexLabel}</Text>}
            <Text as="span">{categoryLabel[project.category]}</Text>
          </Flex>

          <Text
            fontFamily="heading"
            fontWeight="600"
            fontSize={{ base: '22px', md: '24px' }}
            lineHeight="1.2"
            color="text.primary"
            _groupHover={{ color: 'accent.500' }}
            transition="color 0.25s ease"
          >
            {project.name}
          </Text>
          <Text
            mt="6px"
            fontSize="14px"
            color="text.muted"
            maxW="60ch"
            noOfLines={2}
            lineHeight="1.55"
          >
            {project.description}
          </Text>
          <Text
            mt="10px"
            fontFamily="mono"
            fontSize="11px"
            letterSpacing="0.04em"
            color="text.faint"
          >
            {project.techs.slice(0, 4).join(' · ')}
          </Text>
        </GridItem>

        <GridItem gridArea="thumb">
          {project.thumbnail && (
            <>
              {/* Mobile thumb — larger, non-compact mock */}
              <Box
                display={{ base: 'block', md: 'none' }}
                w={isMobileCategory ? '180px' : 'full'}
                h={isMobileCategory ? '320px' : '180px'}
                mx={isMobileCategory ? 'auto' : '0'}
              >
                {isMobileCategory ? (
                  <AndroidMock src={project.thumbnail} alt={project.name} />
                ) : (
                  <SafariMock
                    src={project.thumbnail}
                    alt={project.name}
                    url={project.url ?? undefined}
                  />
                )}
              </Box>

              {/* Desktop thumb — compact, reveals subtly on hover */}
              <Box
                display={{ base: 'none', md: 'block' }}
                w={thumbWidthMd}
                h={thumbHeightMd}
                opacity={0.9}
                transition="opacity 0.3s ease, transform 0.3s ease"
                _groupHover={{ opacity: 1, transform: 'translateY(-2px)' }}
              >
                {isMobileCategory ? (
                  <AndroidMock
                    src={project.thumbnail}
                    alt={project.name}
                    compact
                  />
                ) : (
                  <SafariMock
                    src={project.thumbnail}
                    alt={project.name}
                    compact
                  />
                )}
              </Box>
            </>
          )}
        </GridItem>
      </Grid>
    </Link>
  );
}
